import { useMemo } from 'react';

import type { VideoRoomMetadata, VideoRoomSessionSnapshot } from '@zingizi/video-room';

import type { IClient, IConsultation, IUser } from '../../store/types';

interface UseZingiziSessionAdapterArgs {
  roomName: string;
  consultation?: IConsultation;
  client?: IClient;
  userData?: IUser | null;
  isPsychologist: boolean;
  isGroupConsultationFlag: boolean;
  toolMetadata?: VideoRoomMetadata;
}

export function useZingiziSessionAdapter({
  roomName,
  consultation,
  client,
  userData,
  isPsychologist,
  isGroupConsultationFlag,
  toolMetadata,
}: UseZingiziSessionAdapterArgs): VideoRoomSessionSnapshot | null {
  return useMemo(() => {
    if (!roomName) {
      return null;
    }

    const psychologistName = [userData?.last_name, userData?.first_name, userData?.middle_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    const displayName = isPsychologist ? psychologistName || 'Psychologist' : client?.full_name || 'Client';
    const participantId = isPsychologist
      ? String(userData?.id ?? `psychologist:${roomName}`)
      : String(client?.id ?? `client:${roomName}`);

    return {
      roomName,
      participantId,
      displayName,
      role: isPsychologist ? 'psychologist' : 'client',
      isGroup: isGroupConsultationFlag,
      metadata: {
        consultationId: consultation?.id,
        consultationCode: consultation?.code,
        clientId: client?.id,
        psychologistId: consultation?.psychologist?.id,
        ...toolMetadata,
      },
    };
  }, [
    roomName,
    userData?.last_name,
    userData?.first_name,
    userData?.middle_name,
    userData?.id,
    isPsychologist,
    client?.full_name,
    client?.id,
    isGroupConsultationFlag,
    consultation?.id,
    consultation?.code,
    consultation?.psychologist?.id,
    toolMetadata,
  ]);
}
